import styles from './EntityPickerPopover.module.scss';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Dialog,
  Popover,
  PopoverActions,
  Theme,
  useMediaQuery,
} from '@mui/material';
import React, {
  ForwardedRef,
  forwardRef,
  type JSX,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';
import { settings, useSetting } from '@work4all/data/lib/settings';

import { useForceUpdate } from '@work4all/utils/lib/hooks/use-force-update';

import { ResizableArea, Size } from '../../ResizableArea';
import { IPickerProps } from '../types';

type IEntityPickerPopoverRenderTargetProps = {
  /**
   * Ref muse be passed to the target element. It is used to position the popover.
   *
   * Alternatively you can provide `anchorEl` to the `EntityPickerPopover` which then will
   * be used instead of the target. This can be useful if you want to have one element
   * trigger the popover, but have it overlay some other container.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: React.Ref<any>;
  /**
   * A callback to trigger
   */
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
};

export type IEntityPickerPopoverProps = {
  /**
   * The popover width set directly to popover element.
   */
  width?: number | 'content';
  /**
   * The minimum width for the ResizableArea inside the popover.
   *
   * @default 200
   */
  resizableAreaMinWidth?: number;
  /**
   * The popover won't be shown while components is disabled
   */
  disabled?: boolean;
  /**
   * The function to be called when the popover is opened.
   */
  onOpen?: () => void;
  /**
   * The function to be called when the popover is closed.
   */
  onClose?: () => void;
  /**
   * The function to be called when the popover is clicked.
   */
  onTargetClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * The entity picker to be rendered inside the popover.
   */
  picker: React.ReactElement<IPickerProps<unknown, boolean>>;
  /**
   * By default the popover will be positioned over the target element
   * (passed as `children` or `renderTarget`). This can be used to set
   * any other element as popover target.
   */
  anchorEl?: Element | React.RefObject<Element | null>;
  /**
   * Styles that should be applied to the popover's `paper` element.
   */
  style?: React.CSSProperties;
  /**
   * Class that should be applied to the popover's `paper` element.
   */
  className?: string;
  /**
   * The popover will automatically close after the wrapped picker's value
   * changes if the picker is in single-selection mode.
   * This behavior can be overridden with this prop.
   */
  autoclose?: boolean;
  footer?: React.ReactNode;
  children:
    | JSX.Element
    | ((props: IEntityPickerPopoverRenderTargetProps) => React.ReactNode);
  fullscreen?: boolean;
  pickerType?: string;
  persistSize?: boolean;
};

export type EntityPickerPopover = {
  open: () => void;
  close: () => void;
};

/**
 * Creates a popover that will open when you click on the `children` element.
 *
 * Position of the popover can be customized with `anchorEl`. If it is supplied,
 * the popover will be positioned on top of the specified element.
 * By default positions the popover on top of `children`.
 *
 * `children` can be either an element or a render prop. If you pass an element,
 * make sure it accepts `ref` and `onClick` callback.
 *
 * By default the popover will try to be the same width as the anchor element
 * (either the one passed with `anchorEl` or the child element) but it will
 * not shrink below `min-content`. This behavior can be customized with
 * `className` and `style` props.
 *
 * _Important:_ The calculated `width` is set using inline styles. Prefer
 * using `min-width` and `max-width` properties to limit the popover size.
 */
export const EntityPickerPopover = forwardRef(function EntityPickerPopover(
  props: IEntityPickerPopoverProps,
  ref: ForwardedRef<EntityPickerPopover>
) {
  const {
    picker,
    anchorEl,
    onTargetClick,
    onOpen,
    onClose,
    disabled = false,
    children,
    width: inWidth,
    fullscreen,
    pickerType = 'default',
    persistSize = false,
    resizableAreaMinWidth = 200,
  } = props;

  const [targetEl, setTargetEl] = useState<Element>(null);
  const [open, setOpen] = useState(false);

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  // Change default only if not fullscreen
  const width = fullscreen
    ? inWidth
    : inWidth === undefined
    ? 'content'
    : inWidth;

  // -- Sizes
  const fileType = 'name' in picker.props ? `${picker.props.name}` : pickerType;
  const { set, value } = useSetting(
    settings.pickerSize({
      fileType,
    })
  );

  const [localSize, setLocalSize] = useState<Size<'horizontal'>>({
    width: undefined,
  });

  // Persit size only if enabled
  const size = fullscreen ? undefined : persistSize ? value : localSize;
  const onSizeChange = fullscreen
    ? undefined
    : persistSize
    ? set
    : setLocalSize;
  // -- Sizes
  // -- Move
  const forceUpdate = useForceUpdate();
  const dragging = useRef(false);

  const popoverPosition = useRef({ top: 0, left: 0 });
  const startPosition = useRef({ x: 0, y: 0 });

  // Handle mouse move event
  const handleMouseMove = (e) => {
    const dx = e.clientX - startPosition.current.x;
    const dy = e.clientY - startPosition.current.y;

    const newTop = popoverPosition.current.top + dy;
    const newLeft = popoverPosition.current.left + dx;

    // IF mouse if out of screen do not change value -> return;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    if (
      e.clientY < 0 ||
      windowHeight < e.clientY ||
      e.clientX < 0 ||
      windowWidth < e.clientX
    ) {
      startPosition.current = { x: e.clientX, y: e.clientY };
      forceUpdate();
      return;
    }

    // otherwise we need to update new (x,y) & calculate boundries
    popoverPosition.current = {
      top: newTop,
      left: newLeft,
    };

    validateBoundries(resolvedAnchorEl, popoverPosition);

    startPosition.current = { x: e.clientX, y: e.clientY };
    forceUpdate();
  };

  // Handle mouse up event
  const handleMouseUp = () => {
    dragging.current = false;
    // Remove the event listeners after the drag ends
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  // Handle mouse down event
  const handleMouseDown = (e) => {
    dragging.current = true;
    startPosition.current = { x: e.clientX, y: e.clientY };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    if (open) {
      popoverPosition.current = { top: 0, left: 0 };
      forceUpdate();
    }
  }, [forceUpdate, open]);
  // -- Move;

  const _onClose = useCallback(() => {
    onClose?.();
    if (fullscreen) {
      window.history.back();
    }
  }, [fullscreen, onClose]);

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setOpen(true);
        onOpen?.();
      },
      close: () => {
        setOpen(false);
        _onClose?.();
      },
    }),
    [onOpen, _onClose]
  );

  const popoverActions = useRef<PopoverActions>(null);

  const handleChange = (...args) => {
    if (!open) {
      console.warn('Popover is closed, but picker is trying to change value');
      return;
    }
    picker.props.onChange.apply(undefined, args);

    // If the wrapped picker is in single-selection mode, close the popover
    // after the picker's value changes.

    const autoclose = props.autoclose ?? !picker.props.multiple;

    if (autoclose) {
      setOpen(false);
      _onClose?.();
    }
  };

  const handleResize = () => {
    popoverActions.current?.updatePosition();
  };
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  function getTarget() {
    const openClick = (e: React.MouseEvent<HTMLDivElement>) => {
      onTargetClick && onTargetClick(e);
      if (!disabled) {
        setOpen(true);
        onOpen?.();
      }
    };
    const targetProps: IEntityPickerPopoverRenderTargetProps = {
      ref: setTargetEl,
      onClick: openClick,
    };

    const target =
      typeof children === 'function'
        ? children(targetProps)
        : React.cloneElement(children, {
            ...children.props,
            ...targetProps,
          });
    return target;
  }

  const target = getTarget();

  const resolvedAnchorEl = anchorEl
    ? 'current' in anchorEl
      ? anchorEl.current
      : anchorEl
    : targetEl;

  const pickerTypeName = picker.type?.['displayName'];

  useLayoutEffect(() => {
    if (width === 'content') {
      if (size && size.width === undefined && open) {
        const minSize = MIN_SIZE_BY_PICKER_TYPE[pickerTypeName] || 320;
        onSizeChange({
          width: Math.max(resolvedAnchorEl?.clientWidth, minSize),
        });
      }
      return;
    }
  }, [open, resolvedAnchorEl, width, onSizeChange, size, pickerTypeName]);

  useEffect(() => {
    if (fullscreen && open) {
      window.history.pushState({}, 'enter search');
      window.addEventListener('popstate', () => {
        setOpen(false);
        onClose?.();
      });
      return () =>
        window.addEventListener('popstate', () => {
          setOpen(false);
          onClose?.();
        });
    }
  }, [fullscreen, open, onClose]);

  // When we are out of the screen we need to move it to the left side.
  useEffect(() => {
    if (fullscreen) return;
    if (resolvedAnchorEl) {
      setTimeout(() => {
        if (!open) return;
        const el = document
          .getElementById('picker-container')
          ?.getElementsByClassName(styles['popover-paper-root'])?.[0];
        if (!el) return;

        const resizeObserver = new ResizeObserver((entries) => {
          validateBoundries(resolvedAnchorEl, popoverPosition);
          forceUpdate();
        });

        resizeObserver.observe(el);
      }, 0);

      // Update the popover position if the screen is resized (like when a keyboard is opened on Android)
      const handleResize = () => {
        validateBoundries(resolvedAnchorEl, popoverPosition);
        forceUpdate();
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [open]);

  return (
    <>
      {target}

      {fullscreen ? (
        <Dialog
          data-test-id="picker-container"
          fullScreen
          fullWidth
          open={open}
          classes={{ paper: styles['dialog-paper-root'] }}
          onClose={() => {
            setOpen(false);
            _onClose?.();
          }}
        >
          {React.cloneElement(picker, {
            ...picker.props,
            onChange: handleChange,
            onResize: handleResize,
            onClose: _onClose,
            fullscreen: true,
          })}
          {props.footer}
        </Dialog>
      ) : (
        <Popover
          id="picker-container"
          data-test-id="picker-container"
          action={popoverActions}
          open={open}
          anchorReference="anchorEl"
          anchorEl={resolvedAnchorEl}
          anchorOrigin={{
            horizontal: 'left',
            vertical: 'bottom',
          }}
          // We need to ignore theshold and manage it by hand
          marginThreshold={-9999}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          onClose={() => {
            setOpen(false);
            _onClose?.();
          }}
          classes={{ paper: styles['popover-paper-root'] }}
          style={
            size
              ? {
                  top: popoverPosition.current.top,
                  left: popoverPosition.current.left,
                }
              : undefined
          }
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
        >
          {size ? (
            <ResizableArea
              className={styles.resizable}
              handles="right"
              minWidth={resizableAreaMinWidth}
              maxWidth={'90vw'}
              size={{ width: size?.width }}
              onResize={onSizeChange}
              style={{ position: 'relative', overflow: 'hidden' }}
              hasIcon
            >
              {React.cloneElement(picker, {
                ...picker.props,
                onChange: handleChange,
                onResize: handleResize,
                dragHandlerElement: isMobile ? undefined : (
                  <DragIndicatorIcon
                    style={{ cursor: dragging.current ? 'grabbing' : 'grab' }}
                    onMouseDown={handleMouseDown}
                  />
                ),
              })}
              {props.footer}
            </ResizableArea>
          ) : (
            <>
              {React.cloneElement(picker, {
                ...picker.props,
                onChange: handleChange,
                onResize: handleResize,
              })}
              {props.footer}
            </>
          )}
        </Popover>
      )}
    </>
  );
});

const validateBoundries = (
  resolvedAnchorEl: Element,
  popoverPosition: React.RefObject<{
    top: number;
    left: number;
  }>
) => {
  const marginThreshold = remToPx(1);
  // start calc boundries
  const containerWidth = document.defaultView.innerWidth - marginThreshold;
  const containerHeight = document.defaultView.innerHeight - marginThreshold;
  const rect = resolvedAnchorEl.getBoundingClientRect();

  // first at all we need to find picker container (movable element)
  const container = document.getElementById('picker-container');
  // and find picker itself (fixed on container, but has it own offset)
  const el = container?.getElementsByClassName(
    styles['popover-paper-root']
  )?.[0];
  if (!el) return;
  const { x, y } = container.getBoundingClientRect();

  // Calculate of visible element.
  // As our Container is transformed we need to sum those coorditanes.
  const top = rect.top + y;
  const left = rect.left + x;
  const right = rect.left + el.clientWidth + x;
  const bottom = rect.top + el.clientHeight + y + rect.height;

  // Check all 4 boundries and adjust position of popover
  if (top < marginThreshold) {
    const diff = top - marginThreshold;
    popoverPosition.current.top -= diff;
  } else if (bottom > containerHeight) {
    const diff = bottom - containerHeight;
    popoverPosition.current.top -= diff;
  }
  if (left < marginThreshold) {
    const diff = left - marginThreshold;
    popoverPosition.current.left -= diff;
  } else if (right > containerWidth) {
    const diff = right - containerWidth;
    popoverPosition.current.left -= diff;
  }
};

const MIN_SIZE_BY_PICKER_TYPE = {
  BusinessPartnerPicker: 380,
  ProjectPicker: 340,
  ContactPicker: 340,
};
